import * as React from 'react'
import { faChevronLeftSolid, faChevronRightSolid, FaIcon } from '@/lib/fa'
import { monthKeysShort } from '@/modules/shared/constants/months'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { CurrentSettlementCycleModel } from '@/lib/api/company/configuration/timesheet-basic/useQueryCurrentSettlementCycle'
import { SettlementPeriodSwitcher } from '../settlement/SettlementPeriodSwitcher'
import { DividerHorizontal } from '../DividerHorizontal'

interface Props {
  selectedMonths: string[]
  onConfirm: (selectedMonths: string[]) => void
  settlementCycle?: CurrentSettlementCycleModel | null
  companyId: string
}

const getGroupMonths = (
  year: number,
  monthIndex: number,
  period: number,
): string[] => {
  if (period <= 1) {
    return [`${year}-${String(monthIndex + 1).padStart(2, '0')}`]
  }
  const groupIndex = Math.floor(monthIndex / period)
  const months: string[] = []
  for (let i = 0; i < period; i++) {
    const m = groupIndex * period + i + 1
    if (m <= 12) months.push(`${year}-${String(m).padStart(2, '0')}`)
  }
  return months
}

const buildRange = (year: number, from: number, to: number): string[] => {
  const months: string[] = []
  for (let m = from; m <= to; m++) {
    months.push(`${year}-${String(m).padStart(2, '0')}`)
  }
  return months
}

const MonthMultiPickerTimesheet: React.FC<Props> = ({
  selectedMonths: initialSelectedMonths,
  onConfirm,
  settlementCycle,
  companyId,
}) => {
  const { t, tSafe } = useLocalize()

  const [selectedMonths, setSelectedMonths] = React.useState<string[]>(
    initialSelectedMonths || [],
  )

  const currentYear = new Date().getFullYear()
  const [viewYear, setViewYear] = React.useState<number>(
    () => Number(initialSelectedMonths?.[0]?.split('-')[0]) || currentYear,
  )
  const period = settlementCycle?.periodLengthInMonths ?? 1

  const handleToggleMonth = (monthIndex: number) => {
    if (period <= 1) {
      const monthValue = `${viewYear}-${String(monthIndex + 1).padStart(2, '0')}`
      setSelectedMonths((prev) =>
        prev.includes(monthValue)
          ? prev.filter((m) => m !== monthValue)
          : [...prev, monthValue].sort(),
      )
      return
    }

    const groupMonths = getGroupMonths(viewYear, monthIndex, period)
    const allSelected = groupMonths.every((m) => selectedMonths.includes(m))
    setSelectedMonths(allSelected ? [] : groupMonths)
  }

  const previousPeriod = React.useRef<number | null>(null)
  React.useEffect(() => {
    if (previousPeriod.current === null) {
      previousPeriod.current = period
      return
    }
    if (previousPeriod.current === period) return
    previousPeriod.current = period

    setSelectedMonths((prev) => {
      if (prev.length === 0) return prev
      const [anchorYear, anchorMonth] = prev[0].split('-').map(Number)
      if (!anchorYear || !anchorMonth) return prev
      return getGroupMonths(anchorYear, anchorMonth - 1, period)
    })
  }, [period])

  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null)

  const isMonthHovered = (monthIndex: number): boolean => {
    if (hoveredIndex === null) return false
    return getGroupMonths(viewYear, hoveredIndex, period).includes(
      `${viewYear}-${String(monthIndex + 1).padStart(2, '0')}`,
    )
  }

  const handleConfirm = () => {
    onConfirm(selectedMonths)
  }

  const isMonthSelected = (monthIndex: number) => {
    const monthValue = `${viewYear}-${String(monthIndex + 1).padStart(2, '0')}`
    return selectedMonths.includes(monthValue)
  }

  const quickRanges = React.useMemo(() => {
    const ranges = [
      {
        key: 'this-year',
        label: tSafe('timesheet.quick-range.this-year', 'Cały ten rok'),
        year: currentYear,
        from: 1,
        to: 12,
      },
      {
        key: 'previous-year',
        label: tSafe(
          'timesheet.quick-range.previous-year',
          'Cały poprzedni rok',
        ),
        year: currentYear - 1,
        from: 1,
        to: 12,
      },
      {
        key: 'first-half',
        label: tSafe('timesheet.quick-range.first-half', 'I półrocze'),
        year: viewYear,
        from: 1,
        to: 6,
      },
      {
        key: 'second-half',
        label: tSafe('timesheet.quick-range.second-half', 'II półrocze'),
        year: viewYear,
        from: 7,
        to: 12,
      },
    ]

    return ranges.filter(({ from, to }) => (to - from + 1) % period === 0)
  }, [tSafe, currentYear, viewYear, period])

  const handleQuickRange = (year: number, from: number, to: number) => {
    setViewYear(year)
    setSelectedMonths(buildRange(year, from, to))
  }

  const isQuickRangeActive = (year: number, from: number, to: number) => {
    const range = buildRange(year, from, to)
    return (
      selectedMonths.length === range.length &&
      range.every((m) => selectedMonths.includes(m))
    )
  }

  return (
    <div className="tw-h-fit tw-w-fit tw-rounded-lg tw-bg-white tw-p-4 tw-pb-0 tw-shadow-md">
      <div className="tw-flex tw-gap-3">
        <div className="tw-w-[340px]">
          <div className="tw-flex tw-items-center tw-justify-between tw-pb-2">
            <button
              type="button"
              onClick={() => setViewYear((y) => y - 1)}
              className="tw-rounded tw-p-1.5 tw-text-content-base/60 hover:tw-bg-gray-100"
            >
              <FaIcon icon={faChevronLeftSolid} className="tw-h-3 tw-w-3" />
            </button>

            <span className="tw-text-sm tw-font-semibold tw-text-content-base">
              {viewYear}
            </span>

            <button
              type="button"
              onClick={() => setViewYear((y) => y + 1)}
              className="tw-rounded tw-p-1.5 tw-text-content-base/60 hover:tw-bg-gray-100"
            >
              <FaIcon icon={faChevronRightSolid} className="tw-h-3 tw-w-3" />
            </button>
          </div>

          <div className="tw-grid tw-grid-cols-4 tw-gap-2 tw-pb-2">
            {monthKeysShort.map((m, i) => {
              const isSelected = isMonthSelected(i)
              const isHovered = isMonthHovered(i)
              return (
                <div
                  key={m}
                  onClick={() => handleToggleMonth(i)}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  className={`tw-w-full tw-cursor-pointer tw-rounded tw-border-2 tw-px-3 tw-py-2 tw-text-center tw-font-normal ${
                    isSelected
                      ? '!tw-border-primary tw-bg-secondary tw-font-semibold tw-text-primary'
                      : isHovered
                        ? 'tw-border-transparent tw-bg-secondary tw-font-semibold tw-text-content-base/90'
                        : 'tw-border-transparent tw-bg-transparent tw-text-content-base/90'
                  }`}
                >
                  {t(`${m}`)}
                </div>
              )
            })}
          </div>
        </div>

        <div className="tw-w-[150px] tw-border-l tw-border-gray-200">
          <div className="tw-mb-2 tw-flex tw-h-6 tw-items-center tw-px-2 tw-text-sm tw-font-semibold tw-text-content-base">
            {tSafe('timesheet.quick-range.title', 'Szybki wybór')}
          </div>
          <DividerHorizontal />
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            {quickRanges.map(({ key, label, year, from, to }) => {
              const isActive = isQuickRangeActive(year, from, to)
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleQuickRange(year, from, to)}
                  className={`tw-rounded-r-md tw-px-2 tw-py-1.5 tw-text-left tw-text-sm tw-transition-colors ${
                    isActive
                      ? 'tw-bg-secondary tw-font-semibold tw-text-primary'
                      : 'tw-text-content-base/80 hover:tw-bg-gray-100'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-gray-200 tw-p-3">
        <SettlementPeriodSwitcher companyId={companyId} size="sm" />

        <button
          onClick={handleConfirm}
          disabled={selectedMonths.length === 0}
          className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-90 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
        >
          {t('common.confirm')}
        </button>
      </div>
    </div>
  )
}

export default MonthMultiPickerTimesheet
